// api/perspectivesApi.js
import { normalizeApiUrl } from './apiClient';

export const PERSPECTIVE_ROLES = ['defense', 'prosecutor', 'judge'];

/**
 * Request jurisdiction-aware perspectives (defense / prosecutor / judge)
 * for a legal case.
 *
 * @param {Object} caseData - { caseId, caseDescription, jurisdiction, caseType }
 * @param {string} apiUrl - Backend base URL
 * @param {string} [idToken] - Bearer token for authorization
 */
export const fetchPerspectives = async (caseData, apiUrl, idToken) => {
  const url = normalizeApiUrl(`${apiUrl}/api/v1/analyze-legal/perspectives`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({
      case: caseData,
      roles: PERSPECTIVE_ROLES,
      includeRecommendations: true,
    }),
  });

  if (!response.ok) {
    let message = `Perspectives request failed (${response.status})`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error) message = errorBody.error;
      if (errorBody?.message) message = errorBody.message;
    } catch {
      // ignore body parse failures
    }
    throw new Error(message);
  }

  return response.json();
};

/**
 * Normalize the backend response shape into { perspectives, jurisdiction, metadata }.
 * Returns null when the payload has no usable perspectives.
 */
export const unwrapPerspectives = (response) => {
  const data = response?.data ?? response;
  if (!data || typeof data !== 'object') return null;

  const perspectives = data.perspectives;
  if (!perspectives || typeof perspectives !== 'object') return null;

  return {
    perspectives,
    jurisdiction: data.jurisdiction ?? null,
    metadata: data.metadata ?? null,
  };
};

/**
 * Build the payload the backend expects for a perspectives PDF/email export.
 */
export const buildPerspectivesPayload = (caseData, perspectives, jurisdiction, metadata) => ({
  caseId: caseData?.caseId || 'unknown',
  caseType: caseData?.caseType || 'Not specified',
  jurisdiction: jurisdiction || caseData?.jurisdiction || null,
  perspectives,
  metadata,
});

/**
 * Request a dedicated perspectives PDF and return the export response
 * ({ success, data: { fileName, downloadUrl } }).
 */
export const exportPerspectivesPdf = async (payload, apiUrl, idToken) => {
  const url = normalizeApiUrl(`${apiUrl}/api/v1/export/pdf`);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ type: 'perspectives', perspectivesResult: payload }),
  });

  if (!response.ok) {
    let message = `Perspectives PDF export failed (${response.status})`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error) message = errorBody.error;
    } catch {
      // ignore body parse failures
    }
    throw new Error(message);
  }

  return response.json();
};

/**
 * Email the perspectives report (with a dedicated PDF attachment).
 */
export const emailPerspectives = async (to, payload, apiUrl, idToken) => {
  const url = normalizeApiUrl(`${apiUrl}/api/v1/notify/email`);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ to, includePdf: true, perspectivesResult: payload }),
  });

  if (!response.ok) {
    let message = `Perspectives email failed (${response.status})`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error) message = errorBody.error;
    } catch {
      // ignore body parse failures
    }
    throw new Error(message);
  }

  return response.json();
};
